<div class="dialog-header">
    <h2 mat-dialog-title class="lmc-dialog-title">Manual Transaction</h2>
    <button mat-icon-button [mat-dialog-close]="null" type="button"
            class="close-button" [disabled]="isSubmitting"
            aria-label="Discard and close" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</div>

<mat-dialog-content class="manual-dialog-content">

    <!-- Batch settings -->
    <mat-card appearance="outlined" class="section-card batch-settings">
        <mat-card-content>
            <div class="section-group-label">
                <mat-icon>tune</mat-icon>
                <span>Batch Settings</span>
            </div>

            <form [formGroup]="headerForm" class="settings-grid">
                <mat-form-field appearance="outline">
                    <mat-label>Funding Type</mat-label>
                    <mat-select formControlName="fundingType">
                        <mat-option *ngFor="let o of fundingTypeOptions" [value]="o.value">{{ o.label }}</mat-option>
                    </mat-select>
                    <mat-error *ngIf="headerForm.get('fundingType')?.hasError('required')">
                        Funding type is required.
                    </mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label>Transaction Flow</mat-label>
                    <mat-select formControlName="transactionFlow">
                        <mat-option *ngFor="let f of transactionFlowOptions" [value]="f.value">{{ f.label }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </form>
        </mat-card-content>
    </mat-card>

    <!-- Transactions -->
    <mat-card appearance="outlined" class="section-card transactions-section">
        <mat-card-content>
            <div class="section-header">
                <div class="section-group-label">
                    <mat-icon>receipt_long</mat-icon>
                    <span>Transactions</span>
                    <span class="row-count-chip">{{ rows.length }}</span>
                </div>
                <button mat-stroked-button color="primary" type="button" (click)="addRow()">
                    <mat-icon>add</mat-icon> Add row
                </button>
            </div>

            <div class="table-container">
                <!-- Each transaction is two rows: its inputs, then a fixed-height note line under them. -->
                <table mat-table [dataSource]="rows" [trackBy]="trackByUid" multiTemplateDataRows
                       class="flat-table rows-table">

                    <ng-container matColumnDef="index">
                        <th mat-header-cell *matHeaderCellDef class="col-index">#</th>
                        <td mat-cell *matCellDef="let _row; let i = dataIndex" class="col-index">{{ i + 1 }}</td>
                    </ng-container>

                    <ng-container matColumnDef="loanNumber">
                        <th mat-header-cell *matHeaderCellDef>Loan #</th>
                        <td mat-cell *matCellDef="let row">
                            <mat-form-field appearance="outline" class="cell-field cell-field-loan">
                                <input matInput [(ngModel)]="row.loanNumber" name="ln-{{ row.uid }}"
                                       (blur)="triggerLookupIfReady(row)"
                                       placeholder="Enter loan #" />
                                <mat-spinner matIconSuffix *ngIf="row.lookupState === 'loading'"
                                             diameter="16" class="lookup-spinner"></mat-spinner>
                            </mat-form-field>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="payeeName">
                        <th mat-header-cell *matHeaderCellDef class="col-payee">Payee</th>
                        <td mat-cell *matCellDef="let row" class="col-payee">
                            <mat-form-field appearance="outline" class="cell-field cell-field-payee">
                                <input matInput readonly [value]="row.payeeName"
                                       [disabled]="!hasLoan(row)" placeholder="Payee"
                                       [matTooltip]="row.payeeName" [matTooltipDisabled]="!row.payeeName" />
                            </mat-form-field>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="amount">
                        <th mat-header-cell *matHeaderCellDef class="col-amount">Amount</th>
                        <td mat-cell *matCellDef="let row" class="col-amount">
                            <mat-form-field appearance="outline" class="cell-field cell-field-amount">
                                <span matTextPrefix>$&nbsp;</span>
                                <input matInput type="number" min="0" step="0.01"
                                       [(ngModel)]="row.amount" name="amt-{{ row.uid }}"
                                       [disabled]="!hasLoan(row)"
                                       placeholder="0.00" />
                            </mat-form-field>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="paymentDate">
                        <th mat-header-cell *matHeaderCellDef>Payment Date</th>
                        <td mat-cell *matCellDef="let row">
                            <mat-form-field appearance="outline" class="cell-field cell-field-date">
                                <input matInput [matDatepicker]="rowDp"
                                       [min]="minPaymentDate"
                                       [(ngModel)]="row.paymentDate" name="dt-{{ row.uid }}"
                                       [disabled]="!hasLoan(row)"
                                       placeholder="Select date" />
                                <mat-datepicker-toggle matIconSuffix [for]="rowDp"
                                                       [disabled]="!hasLoan(row)"></mat-datepicker-toggle>
                                <mat-datepicker #rowDp></mat-datepicker>
                            </mat-form-field>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="bankAccount">
                        <th mat-header-cell *matHeaderCellDef class="col-bank">Banking Account</th>
                        <td mat-cell *matCellDef="let row" class="col-bank">
                            <mat-form-field appearance="outline" class="cell-field bank-select">
                                <mat-select #bankSelect [ngModel]="row.selectedBankAccount"
                                            (ngModelChange)="onBankAccountChange(row, $event, bankSelect)"
                                            [disabled]="!hasLoan(row)"
                                            name="ach-{{ row.uid }}" [placeholder]="bankPlaceholder(row)">
                                    <mat-option *ngFor="let a of row.bankAccounts" [value]="a">
                                        {{ bankAccountLabel(a) }}
                                    </mat-option>
                                    <mat-option [value]="CUSTOM_OPTION" class="custom-option">
                                        <mat-icon>add</mat-icon> Custom account…
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>
                        </td>
                    </ng-container>

                    <!-- Note line: blank under #, then spanning every input column. The row tint
                         carries the outcome; the note names it and adds the detail. -->
                    <ng-container matColumnDef="noteIndex">
                        <td mat-cell *matCellDef="let _row" class="col-index"></td>
                    </ng-container>

                    <ng-container matColumnDef="note">
                        <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length - 1">
                            <div class="note-line">
                                <span *ngIf="rowNote(row) as note" class="note-text"
                                      [matTooltip]="note">{{ note }}</span>
                            </div>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef class="col-actions"></th>
                        <td mat-cell *matCellDef="let row" class="col-actions">
                            <button mat-icon-button color="warn" type="button"
                                    (click)="removeRow(row)" matTooltip="Remove row">
                                <mat-icon>delete_outline</mat-icon>
                            </button>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="input-row"
                        [class.row-blocked]="isRowBlocked(row)"></tr>
                    <tr mat-row *matRowDef="let row; columns: noteColumns;" class="note-row"
                        [class.row-blocked]="isRowBlocked(row)"></tr>
                </table>
            </div>
        </mat-card-content>
    </mat-card>

</mat-dialog-content>

<mat-dialog-actions class="dialog-actions">
    <div class="summary-bar">
        <span class="ready-count" [class.all-ready]="rows.length > 0 && readyRowCount === rows.length">
            <mat-icon>check_circle</mat-icon>
            {{ readyRowCount }} / {{ rows.length }} ready
        </span>
        <span class="total-amount">
            Total <strong>{{ FormatUtil.formatCurrency(totalAmount) }}</strong>
        </span>
    </div>

    <button mat-raised-button color="primary" type="button"
            (click)="onSubmit()"
            [disabled]="!canSubmit">
        <mat-spinner *ngIf="isSubmitting" diameter="18" class="btn-spinner"></mat-spinner>
        Submit Batch
    </button>
</mat-dialog-actions>